import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Card, Form, Button, Table, message, Modal, Space, Tag, InputNumber, Checkbox, Radio, Row, Col,
  Typography, Switch, Input, Alert, Tooltip, Popover, Descriptions, Select,
} from 'antd';
import { PlusOutlined, MinusCircleOutlined, QuestionCircleOutlined, ShopOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { WarehouseDetail } from '../../../types/warehouse';
import { getAllWarehouses, getWarehouseDetail } from '../../../services/warehouse';
import { getShippingRateQuote, searchCustomers } from '../../../services/logistics';
import type { ShippingRateQuoteResult, AddressPayload, PackagePayload, CustomerOption } from '../../../services/logistics';
import SectionCard, { SECTION_COLORS } from './SectionCard';
import PartyInfoPanel from './PartyInfoPanel';
import PurchaseModal from './PurchaseModal';
import UnitToggle from './UnitToggle';
import type { UnitSystem, PackageFormValue, WeightBreakdown } from './units';
import { convertPackageUnits, packageToMetric, sumWeightNative, sumVolumeNative, formatWeightBreakdown, formatMoney } from './units';
import { useAuth } from '../../../hooks/useAuth';

const { Text } = Typography;

// 分组显示时，同一组的所有记录用同一个背景色区分（不管当前是"按网关分组"还是"按物流产品
// 分组"）——颜色本身没有业务含义，纯粹是浅色调、循环使用，分组数量超过颜色数量时从头循环，
// 不会因为分组多就报错或留白。
const GROUP_ROW_COLORS = ['#e6f4ff', '#f6ffed', '#fff7e6', '#fff0f6', '#f9f0ff', '#e6fffb', '#fcffe6', '#fff1f0'];

type GroupMode = 'gateway' | 'product';

const BATTERY_INSTALLATION_LABELS: Record<string, string> = {
  installed: '安装在设备内',
  packed: '随设备运输但未安装',
  standalone: '单独电池',
};


interface QuoteFormValues {
  origin: AddressPayload;
  destination: AddressPayload;
  packages: PackageFormValue[];
  signature_required?: boolean;
  insurance_required?: boolean;
  insurance_amount?: number;
  contains_other_dangerous_goods?: boolean;
  battery_declared?: boolean;
  battery_installation?: 'installed' | 'packed' | 'standalone';
  battery_chemistry?: 'lithium_ion' | 'lithium_metal';
  battery_condition_confirmed?: boolean;
  reference_number?: string;
  remarks?: string;
}

// "复制运单"功能用：从物流记录列表页带过来的预填数据（通过 navigate 的 state 传递，
// 不经过后端）。只包含可以直接照抄的内容——电池危险品条件确认（battery_condition_confirmed）
// 是一次性的安全声明，不放在这里，每次都必须让用户自己重新勾选，不能预填。
export interface ShipmentCopyPrefill {
  origin: AddressPayload;
  destination: AddressPayload;
  packages: PackageFormValue[];
  signature_required: boolean;
  insurance_required: boolean;
  insurance_amount?: number;
  battery_declared: boolean;
  battery_chemistry?: 'lithium_ion' | 'lithium_metal';
  battery_installation?: 'installed' | 'packed' | 'standalone';
  remarks: string;
}

export interface PurchaseTarget {
  quote: ShippingRateQuoteResult;
  signature_required?: boolean;
  insurance_amount?: number;
  battery_declared?: boolean;
  battery_chemistry?: 'lithium_ion' | 'lithium_metal';
  battery_installation?: 'installed' | 'packed' | 'standalone';
}

// 购买成功弹窗里每个包裹要显示"这个包裹对应询价时的第几个包裹类型、那个类型的重量/尺寸"——
// 询价提交时按 quantity 把每个包裹类型展开成若干个独立包裹（见 runQuote 里的展开逻辑），
// 这里同步记一份跟展开后的 packages 数组严格同序、等长的"出身信息"，购买弹窗按下标对应即可，
// 不需要在后端/RateQuote 上额外存这个纯展示用的信息。
export interface PackageOriginInfo {
  /** 0-based，对应询价表单 Form.List 里第几个包裹类型（展示时 +1） */
  typeIndex: number;
  weightLabel: string;
  dimsLabel: string;
}

export interface QuoteOverview {
  originCountry: string;
  originPostalCode: string;
  destinationCountry: string;
  destinationPostalCode: string;
  /** 只有收件国家是 US 时才有意义；null 表示未判定（非美国地址，或本次没有查到任何报价） */
  isResidential: boolean | null;
  packageCount: number;
  /** 查询时选择的单位下原生算好、可以直接展示的总重量/总体积，不需要再额外换算。
   * 不随后续对"包裹信息"单位开关的切换实时变化——切换单位不会自动重新计算这两个数，
   * 要看新单位下的总重量/总体积需要重新查询一次。 */
  totalWeightDisplay: WeightBreakdown;
  totalVolumeDisplay: number;
  overviewUnitSystem: UnitSystem;
  signatureRequired: boolean;
  insuranceRequired: boolean;
  insuranceAmount?: number;
  batteryDeclared: boolean;
  batteryInstallation?: 'installed' | 'packed' | 'standalone';
}

// 当前物流渠道只支持美国境内运输（发件、收件地址都必须是美国），后端在询价/购买两处
// 都会做同样的强制校验（见 route_support_service.py）——这里只是让用户在填表这一步就
// 选不出不支持的组合，不用等提交后才被拒绝。手动国家下拉框、仓库快捷填入、地址库导入
// 三处入口都读这一个常量，不要各写一份；以后开放其它地区业务时，改这一个常量即可。
const ALLOWED_COUNTRIES = ['US'];

// 姓名字段（发件人/收件人姓名）故意不填：即使用仓库地址，收发件人姓名仍需要手动填写。
const warehouseToAddress = (w: WarehouseDetail): AddressPayload => ({
  country: w.country,
  postal_code: w.postal_code,
  state: w.state,
  city: w.city,
  address_line: w.address_line1,
  address_line2: w.address_line2,
  company: w.company_name,
  phone: w.contact_phone,
  email: w.contact_email,
});

const ParcelRateQuote: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isCustomer = user?.role === 'customer';
  const location = useLocation();
  const navigate = useNavigate();
  const [form] = Form.useForm<QuoteFormValues>();
  const [warehouses, setWarehouses] = useState<WarehouseDetail[]>([]);
  const [warehousesLoading, setWarehousesLoading] = useState(false);
  const [quoting, setQuoting] = useState(false);
  const [results, setResults] = useState<ShippingRateQuoteResult[]>([]);
  const [hasQueried, setHasQueried] = useState(false);
  // 报价一旦返回（不管有没有结果），表单整体锁定，禁止再手动修改地址/包裹/签名/保险/电池
  // 等任何会影响报价内容的字段——避免"页面上看着是新地址、实际购买用的还是旧报价"这种
  // 漂移。要改信息必须先点"重新询价"（清空当前结果、解锁表单），改完再重新查询一次。
  const [locked, setLocked] = useState(false);
  const [quoteOverview, setQuoteOverview] = useState<QuoteOverview | null>(null);
  const [packageOrigins, setPackageOrigins] = useState<PackageOriginInfo[]>([]);
  const [purchaseTarget, setPurchaseTarget] = useState<PurchaseTarget | null>(null);
  const [quickQuote, setQuickQuote] = useState(false);
  const [unitSystem, setUnitSystem] = useState<UnitSystem>('imperial');
  // 仅管理员账号可见/可用："按网关分组"：先按 Gateway 分组，组间按该组最低价排序，组内再按价格升序；
  // "按物流产品分组"：把不同网关返回的同一个物流产品（承运商+物流产品相同）归到一组，
  // 组间同样按该组最低价排序，组内按价格升序——方便直接对比同一个产品在不同网关的报价。
  const [groupMode, setGroupMode] = useState<GroupMode>('gateway');
  // 仅管理员账号可见/可用：打开后跳过后端"跨 Gateway 去重、同一承运商/服务只保留最低价"，
  // 返回所有 Gateway 的所有报价——用于排查某个 Gateway 是否正常工作。这个开关改变的是
  // 服务端返回的数据本身，不是纯前端展示顺序，所以切换时如果已经查询过一次，会用当前
  // 表单条件自动重新查询一次（见 handleToggleShowAllGateways）。
  const [showAllGateways, setShowAllGateways] = useState(true);
  // 承运商筛选：多选，为空表示不筛选（显示全部）
  const [carrierFilter, setCarrierFilter] = useState<string[]>([]);
  // 物流产品筛选：多选，为空表示不筛选（显示全部）；跟承运商筛选是两个独立维度，同时选中时取交集
  const [productFilter, setProductFilter] = useState<string[]>([]);
  // 员工/管理员代客户询价时可选指定客户（不是必填）：指定了就按这个客户真实的风险档案
  // 算保证金预估、筛选国际件；不指定则按系统默认值给一个通用预估，见下方询价结果提示。
  // 客户账号自己询价不需要这组状态，后端会按登录身份自动解析。
  const [selectedQuoteCustomer, setSelectedQuoteCustomer] = useState<CustomerOption | undefined>(undefined);
  const [quoteCustomerOptions, setQuoteCustomerOptions] = useState<CustomerOption[]>([]);
  const [quoteCustomerSearchLoading, setQuoteCustomerSearchLoading] = useState(false);
  const quoteCustomerSearchTimerRef = React.useRef<number | null>(null);
  const handleQuoteCustomerSearch = (keyword: string) => {
    if (quoteCustomerSearchTimerRef.current) {
      window.clearTimeout(quoteCustomerSearchTimerRef.current);
    }
    quoteCustomerSearchTimerRef.current = window.setTimeout(async () => {
      setQuoteCustomerSearchLoading(true);
      try {
        setQuoteCustomerOptions(await searchCustomers(keyword));
      } catch (error) {
        console.error('查询客户列表失败:', error);
      } finally {
        setQuoteCustomerSearchLoading(false);
      }
    }, 300);
  };
  const insuranceRequired = Form.useWatch('insurance_required', form);
  const containsOtherDangerousGoods = Form.useWatch('contains_other_dangerous_goods', form);
  const batteryDeclared = Form.useWatch('battery_declared', form);
  const destinationCountry = Form.useWatch(['destination', 'country'], form);
  // 签名服务只有美国收件地址才支持直接选择，非美国地址时禁用勾选框（但问号提示始终可以 hover 查看）
  const signatureDisabledForNonUS = destinationCountry !== 'US';

  useEffect(() => {
    if (signatureDisabledForNonUS) {
      form.setFieldsValue({ signature_required: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signatureDisabledForNonUS]);

  useEffect(() => {
    const loadWarehouses = async () => {
      setWarehousesLoading(true);
      try {
        const list = await getAllWarehouses();
        setWarehouses(list || []);
      } catch (error) {
        message.error('获取仓库列表失败');
        console.error('获取仓库列表失败:', error);
      } finally {
        setWarehousesLoading(false);
      }
    };
    loadWarehouses();
  }, []);

  // "复制运单"：从物流记录列表页带 state 跳转过来时，把已购买运单的地址/包裹/其他信息
  // 直接灌进表单，不用重新手动填一遍。只在进入页面时读一次；读完立刻把 state 清掉，
  // 避免用户刷新这个页面时，因为 location.state 还在而把表单重新填一遍、盖掉用户已经改过的内容。
  useEffect(() => {
    const prefill = (location.state as { shipmentPrefill?: ShipmentCopyPrefill } | null)?.shipmentPrefill;
    if (!prefill) return;
    setUnitSystem('metric');
    form.setFieldsValue({
      origin: prefill.origin,
      destination: prefill.destination,
      packages: prefill.packages,
      signature_required: prefill.signature_required,
      insurance_required: prefill.insurance_required,
      insurance_amount: prefill.insurance_amount,
      battery_declared: prefill.battery_declared,
      battery_chemistry: prefill.battery_chemistry,
      battery_installation: prefill.battery_installation,
      remarks: prefill.remarks,
    });
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 非美国仓库（如果有）不应该出现在"从本司仓库发货/发货至本司仓库"这个快捷填入下拉框
  // 里——这条路径是直接把仓库地址整个塞进表单，不经过下面的国家下拉框，必须在这里单独
  // 过滤，不能指望国家下拉框的 restrictCountriesTo 能拦住它。
  const warehouseOptions = warehouses
    .filter((w) => ALLOWED_COUNTRIES.includes(w.country))
    .map((w) => ({ value: w.id, label: `${w.code} - ${w.name}` }));

  const fillFromWarehouse = async (side: 'origin' | 'destination', warehouseId: number | undefined) => {
    // 下拉支持清空（allowClear），清空时 antd 会回调 undefined——这不是一次真正的选择，
    // 不需要请求详情接口，也不应该报错。
    if (!warehouseId) return;
    // 列表接口（getAllWarehouses）用的是精简序列化，没有邮编/州省/详细地址，
    // 快捷填入必须重新拉一次详情接口才能拿到完整地址。
    try {
      const response = await getWarehouseDetail(warehouseId);
      form.setFieldsValue({ [side]: warehouseToAddress(response.data) } as Partial<QuoteFormValues>);
    } catch (error) {
      message.error('获取仓库详细地址失败');
      console.error('获取仓库详细地址失败:', error);
    }
  };

  const handleUnitToggle = (target: UnitSystem) => {
    const currentPackages: PackageFormValue[] = form.getFieldValue('packages') || [];
    const converted = currentPackages.map((pkg) => convertPackageUnits(pkg || {}, target));
    form.setFieldsValue({ packages: converted });
    setUnitSystem(target);
  };

  /** 保险金额按包裹数量平均分摊；除不尽的余数加到第一个包裹上（仅用于展示分摊提示，实际分摊由后端计算） */
  const describeInsuranceSplit = (totalAmount: number, packageCount: number) => {
    if (packageCount <= 1) return `保险金额 ${totalAmount} 美金`;
    const base = Math.floor(totalAmount / packageCount);
    const remainder = totalAmount - base * packageCount;
    return `${packageCount} 个包裹平均分摊，每个约 ${base} 美金${remainder > 0 ? `（第 1 个包裹多分摊 ${remainder} 美金余数）` : ''}`;
  };

  const handleSubmit = async (values: QuoteFormValues) => {
    await runQuote(values, showAllGateways);
  };

  // 抽出来单独接收 includeAllGateways 参数（而不是在函数体内读 showAllGateways 这个 state），
  // 是为了给"显示所有网关报价"开关的自动重新查询用：那个场景下开关刚切换、state 更新和
  // 本次查询几乎同时发生，直接读 state 可能读到切换前的旧值，显式传参没有这个时序问题。
  const runQuote = async (values: QuoteFormValues, includeAllGateways: boolean) => {
    if (values.contains_other_dangerous_goods) {
      // 公司政策：除电池外的其它危险品不支持任何物流产品运输——前端直接拦截，
      // 连询价请求都不发，不消耗任何 Gateway 调用额度（后端入口也有同样的兜底校验）。
      Modal.warning({
        title: '暂不支持',
        content: '暂不支持除电池以外的危险品运输，无法查询报价，请联系本司客服',
        centered: true,
      });
      return;
    }
    setQuoting(true);
    setHasQueried(true);
    try {
      // 磅/公斤允许留空，留空按 0 处理，并把 0 回填到输入框，让用户看到实际使用的值
      const normalizedPackages = (values.packages || []).map((pkg) => {
        const next = { ...pkg };
        if (unitSystem === 'metric' && next.weight_kg == null) next.weight_kg = 0;
        if (unitSystem === 'imperial' && next.weight_lb == null) next.weight_lb = 0;
        return next;
      });
      form.setFieldsValue({ packages: normalizedPackages });

      const packages: PackagePayload[] = [];
      // 和 packages 严格同序、等长——按包裹类型的 quantity 展开时，每展开一个就同步记一条
      // "这个包裹来自第几个包裹类型、那个类型的重量/尺寸是多少"，购买成功后按下标对应展示。
      const origins: PackageOriginInfo[] = [];
      normalizedPackages.forEach((pkg, typeIndex) => {
        const metric = packageToMetric(pkg || {}, unitSystem);
        if (metric.weight_kg == null) return;
        const quantity = Math.max(1, Math.round(pkg.quantity || 1));
        const weightLabel = unitSystem === 'metric'
          ? `${pkg.weight_kg || 0}公斤${pkg.weight_g || 0}克`
          : `${pkg.weight_lb || 0}磅${pkg.weight_oz || 0}盎司`;
        const dimsLabel = unitSystem === 'metric'
          ? `${pkg.length_cm ?? '-'}×${pkg.width_cm ?? '-'}×${pkg.height_cm ?? '-'}厘米`
          : `${pkg.length_in ?? '-'}×${pkg.width_in ?? '-'}×${pkg.height_in ?? '-'}英寸`;
        for (let i = 0; i < quantity; i += 1) {
          packages.push({
            weight_kg: metric.weight_kg,
            length_cm: metric.length_cm,
            width_cm: metric.width_cm,
            height_cm: metric.height_cm,
          });
          origins.push({ typeIndex, weightLabel, dimsLabel });
        }
      });
      if (packages.length === 0) {
        message.error('请至少填写一个包裹的重量');
        setQuoting(false);
        return;
      }

      // 快速询价模式下电话号码这一行本身就不显示，真实调用网关验证过：电话号码留空会导致
      // 部分承运商（比如 FedEx SmartPost）单独报错拒绝报价——这里补一个占位号码，不需要用户
      // 为了这一个隐藏字段特意切到完整地址模式去填。
      const origin = quickQuote && !values.origin?.phone
        ? { ...values.origin, phone: '000-000-0000' } : values.origin;
      const destination = quickQuote && !values.destination?.phone
        ? { ...values.destination, phone: '000-000-0000' } : values.destination;

      const response = await getShippingRateQuote({
        origin,
        destination,
        packages,
        signature_required: values.signature_required,
        insurance_amount: values.insurance_required ? values.insurance_amount : undefined,
        battery_declared: values.battery_declared,
        battery_chemistry: values.battery_declared ? values.battery_chemistry : undefined,
        battery_installation: values.battery_declared ? values.battery_installation : undefined,
        // 非管理员传了也会被后端强制忽略，这里仍然只在管理员账号下才传，语义更清楚
        include_all_gateways: isAdmin ? includeAllGateways : undefined,
        // 客户账号自己询价不需要传，后端按登录身份自动解析；员工/管理员没有指定客户时
        // 传 undefined，走系统默认预估（见结果区的提示文案）。
        customer_id: !isCustomer ? selectedQuoteCustomer?.id : undefined,
      });
      setResults(response.data);
      setPackageOrigins(origins);
      setLocked(true);
      setCarrierFilter([]); // 新一轮查询结果的承运商/物流产品集合可能完全变了，筛选条件不该沿用上一次的
      setProductFilter([]);
      if (response.data.length === 0) {
        message.info('没有查询到可用的物流报价');
      }

      const totalWeightDisplay = sumWeightNative(normalizedPackages, unitSystem);
      const totalVolumeDisplay = sumVolumeNative(normalizedPackages, unitSystem);
      setQuoteOverview({
        originCountry: values.origin?.country || '',
        originPostalCode: values.origin?.postal_code || '',
        destinationCountry: values.destination?.country || '',
        destinationPostalCode: values.destination?.postal_code || '',
        isResidential: response.data[0]?.is_residential ?? null,
        packageCount: packages.length,
        totalVolumeDisplay,
        totalWeightDisplay,
        overviewUnitSystem: unitSystem,
        signatureRequired: !!values.signature_required,
        insuranceRequired: !!values.insurance_required,
        insuranceAmount: values.insurance_required ? values.insurance_amount : undefined,
        batteryDeclared: !!values.battery_declared,
        batteryInstallation: values.battery_declared ? values.battery_installation : undefined,
      });
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      // 未开通国际运单购买权限这一种错误，后端固定用 403 区分（其它询价参数类错误都是
      // 400），用弹窗提醒（比一闪而过的顶部提示更醒目），不用跟其它询价失败原因搞混。
      if (error?.response?.status === 403) {
        Modal.error({ title: '暂无法查询该路线', content: detail || '该账号暂未开通国际运单购买权限，如需开通请联系客服', centered: true });
      } else {
        message.error(detail || '查询报价失败');
      }
      console.error('查询报价失败:', error);
      setResults([]);
      setQuoteOverview(null);
      setPackageOrigins([]);
    } finally {
      setQuoting(false);
    }
  };

  const handleToggleShowAllGateways = async (checked: boolean) => {
    setShowAllGateways(checked);
    if (checked) setGroupMode('gateway'); // 打开后同名报价会一次出现好几条，顺带切到按网关分组避免挤在一起不好看
    if (!hasQueried) return; // 还没查询过，等用户下次点"查询报价"时自然会带上新的开关状态
    try {
      const values = await form.validateFields();
      await runQuote(values, checked);
    } catch {
      // 表单校验没通过（比如必填字段被清空了），维持当前已有的结果不动，不重复弹校验错误
    }
  };

  // "重新询价"：只负责解锁表单、清空上一轮的报价结果，不自动重新提交——用户改完信息后
  // 还是要正常点一次"查询报价"，避免这个按钮变成"一键解锁+悄悄用旧值再查一次"的魔法操作。
  const handleRequote = () => {
    setLocked(false);
    setResults([]);
    setQuoteOverview(null);
    setPackageOrigins([]);
    setHasQueried(false);
    setCarrierFilter([]);
    setProductFilter([]);
  };

  // "创建新运单"：跟"修改询价信息"不一样，不是保留已填内容接着改，而是彻底清空表单
  // （地址、包裹、签名/保险/电池声明全部回到初始状态），重新开始填一票新的。
  const handleCreateNewQuote = () => {
    handleRequote();
    form.resetFields();
    setQuickQuote(false);
  };

  const openPurchase = (quote: ShippingRateQuoteResult) => {
    const values = form.getFieldsValue();
    setPurchaseTarget({
      quote,
      signature_required: values.signature_required,
      insurance_amount: values.insurance_required ? values.insurance_amount : undefined,
      battery_declared: values.battery_declared,
      battery_chemistry: values.battery_declared ? values.battery_chemistry : undefined,
      battery_installation: values.battery_declared ? values.battery_installation : undefined,
    });
  };

  // 同一个承运商+同一个物流产品（carrier_name+service_name）里，Gateway 原始成本价最低的
  // 那一批记录（可能不止一条，价格打平时全部都算）——只在打开"显示所有网关报价"时才计算/展示：
  // 开关关闭时后端已经去重成每个产品最多一条记录，谈"打平"没有意义，这个标识也不该出现。
  // 只按 cost_price 判定"最低"，不看卖价（卖价受各网关加价策略影响，不能反映成本价本身是否打平）：
  // 先找出每个分组里的最小成本价，再一次性收集所有等于这个最小值的记录，全部标"最低"；
  // 打平数量大于 1 时额外标出"N条"，一眼看出这个产品有几个网关报出了并列最低的成本价。
  const lowestCostPriceGroups = useMemo(() => {
    const map = new Map<string, { minCost: number; records: Set<ShippingRateQuoteResult> }>();
    if (!isAdmin || !showAllGateways) return map;
    results.forEach((r) => {
      if (r.cost_price == null) return;
      const key = `${r.carrier_name}|${r.service_name}`;
      const cost = Number(r.cost_price);
      const group = map.get(key);
      if (!group || cost < group.minCost) {
        map.set(key, { minCost: cost, records: new Set([r]) });
      } else if (cost === group.minCost) {
        group.records.add(r);
      }
    });
    return map;
  }, [results, isAdmin, showAllGateways]);

  const columns: ColumnsType<ShippingRateQuoteResult> = [
    // 报价结果对客户/员工是白标的，Gateway 列只对管理员账号可见
    ...(isAdmin ? [{ title: 'Gateway', dataIndex: 'gateway_name', key: 'gateway_name', width: 120 } as const] : []),
    {
      title: '承运商',
      dataIndex: 'carrier_name',
      key: 'carrier_name',
      width: 190,
      render: (text: string, record) => (
        <Space>
          <div
            style={{
              width: 44, height: 44, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {record.carrier_logo ? (
              <img src={record.carrier_logo} alt={text} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            ) : (
              <ShopOutlined style={{ color: '#bfbfbf', fontSize: 22 }} />
            )}
          </div>
          <span>{text}</span>
        </Space>
      ),
    },
    {
      title: '物流产品',
      dataIndex: 'service_name',
      key: 'service_name',
      width: 200,
      render: (text: string, record) => (
        <Space>
          <span>{text}</span>
          {!record.is_mapped && <Tag color="default">未标准化</Tag>}
        </Space>
      ),
    },
    {
      title: '运单费用',
      key: 'price',
      width: 140,
      render: (_, record) => formatMoney(record.price, record.currency),
    },
    {
      title: (
        <Space size={4}>
          风险保证金
          <Tooltip title="购买运单时，系统会按该物流产品预设的保证金规则，在扣除运单费用的同时额外冻结这笔资金，用于覆盖承运商事后重量/尺寸复核产生的运费补差价，降低产生坏账的风险。每个运单的保证金专款专用，购买满 60 天后自动解冻并计入账户可用余额，不受这个运单期间是否发生运费调整影响；期间如产生运费补缴/退款，直接从账户可用余额扣除/退回，不会动用保证金。">
            <QuestionCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
          </Tooltip>
        </Space>
      ),
      key: 'deposit_amount',
      width: 140,
      render: (_, record) => (
        record.deposit_amount == null ? '-' : formatMoney(record.deposit_amount, record.currency)
      ),
    },
    {
      title: '总费用（含风险保证金）',
      key: 'total_with_deposit',
      width: 190,
      render: (_, record) => {
        const total = record.deposit_amount == null
          ? Number(record.price)
          : Number(record.price) + Number(record.deposit_amount);
        return formatMoney(total, record.currency);
      },
    },
    // Gateway 原始成本价 / 理论利润：和 Gateway 列一样，只对管理员账号可见
    ...(isAdmin ? [{
      title: 'Gateway原始价格',
      key: 'cost_price',
      width: 160,
      render: (_: any, record: ShippingRateQuoteResult) => {
        if (record.cost_price == null) return '-';
        const key = `${record.carrier_name}|${record.service_name}`;
        const group = lowestCostPriceGroups.get(key);
        const isLowest = !!group && group.records.has(record);
        const tieCount = group ? group.records.size : 0;
        return (
          <Space size={4}>
            <span>{formatMoney(record.cost_price, record.currency)}</span>
            {isLowest && <Tag color="green">{tieCount > 1 ? `最低 ${tieCount}条` : '最低'}</Tag>}
          </Space>
        );
      },
    } as const] : []),
    ...(isAdmin ? [{
      title: '理论利润',
      key: 'theoretical_profit',
      width: 140,
      render: (_: any, record: ShippingRateQuoteResult) => {
        if (record.cost_price == null) return '-';
        const profit = Number(record.price) - Number(record.cost_price);
        return (
          <Text type={profit >= 0 ? 'success' : 'danger'}>
            {profit < 0 ? '-' : ''}{formatMoney(Math.abs(profit), record.currency)}
          </Text>
        );
      },
    } as const] : []),
    {
      title: '预计时效',
      key: 'transit_days',
      width: 140,
      render: (_, record) => {
        if (record.transit_days_min == null) return '-';
        if (record.transit_days_max != null && record.transit_days_max !== record.transit_days_min) {
          return `${record.transit_days_min}-${record.transit_days_max} 天`;
        }
        return `${record.transit_days_min} 天`;
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Tooltip title={quickQuote ? '当前处于快速询价状态，缺少完整地址信息，请点击"修改询价信息"并关闭快速询价开关后重新查询报价' : ''}>
          <Button type="link" disabled={quickQuote} onClick={() => openPurchase(record)}>
            购买
          </Button>
        </Tooltip>
      ),
    },
  ];

  // 承运商筛选下拉框的选项，从本次查询结果里去重取出——不是固定列表，只列出这次真的报出价的承运商。
  // 显示"名称-代码"（如"联邦快递-FedEx"）；未标准化的记录 name/code 是同一个原始值，不重复显示。
  const carrierOptions = useMemo(() => {
    const byName = new Map<string, string | undefined>();
    results.forEach((r) => {
      if (r.carrier_name && !byName.has(r.carrier_name)) byName.set(r.carrier_name, r.carrier_code);
    });
    return Array.from(byName.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, code]) => ({
        value: name,
        label: code && code !== name ? `${name}-${code}` : name,
      }));
  }, [results]);

  // 物流产品筛选下拉框的选项，同样从本次查询结果里去重取出，不是固定列表。
  // 有意不按当前承运商筛选结果来生成选项（而是始终基于完整的 results）：这样切换承运商筛选
  // 不会导致物流产品筛选框的可选项跟着抖动/消失，两个筛选框各自独立、互不影响对方的可选范围。
  const productOptions = useMemo(() => {
    const names = new Set<string>();
    results.forEach((r) => {
      if (r.service_name) names.add(r.service_name);
    });
    return Array.from(names)
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ value: name, label: name }));
  }, [results]);

  // 分组用的 key：按网关分组时是网关名；按物流产品分组时是"承运商+物流产品"，
  // 这样不同网关报出的同一个产品（比如都报了 UPS Ground）会被归到同一组。
  const groupKeyOf = (r: ShippingRateQuoteResult) => (
    groupMode === 'gateway' ? (r.gateway_name || '') : `${r.carrier_name}|${r.service_name}`
  );

  // 先按承运商、物流产品筛选（两个筛选框是独立维度，同时选中时取交集），筛选后的结果依然是
  // 价格升序（原始 results 本来就是升序，filter 不改变相对顺序）；非管理员直接维持这个默认的
  // "按价格升序"排列；管理员账号下按当前选择的分组方式（按网关/按物流产品）分组——
  // 组间按该组最低价排序，让整体依然大致"便宜的靠前"，组内再按价格升序。
  // 注意：这里显式分桶再拼接，不能简单用 Array.sort 的比较器直接比"组最低价"——
  // 如果两组的最低价刚好相同，比较器会打平，JS 的稳定排序会保留它们在原数组里的相对顺序，
  // 而原数组是按全局价格排的、两组本来就交替出现，结果就是"打平的两组"实际上没有真正分开、
  // 还是交替的——分桶再拼接从根上避免这个问题。
  const displayedResults = useMemo(() => {
    let filtered = results;
    if (carrierFilter.length > 0) filtered = filtered.filter((r) => carrierFilter.includes(r.carrier_name));
    if (productFilter.length > 0) filtered = filtered.filter((r) => productFilter.includes(r.service_name));
    if (!isAdmin) return filtered;
    const groups = new Map<string, ShippingRateQuoteResult[]>();
    filtered.forEach((r) => {
      const key = groupKeyOf(r);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(r);
    });
    const orderedKeys = Array.from(groups.keys()).sort((keyA, keyB) => {
      const minA = Math.min(...groups.get(keyA)!.map((r) => Number(r.price)));
      const minB = Math.min(...groups.get(keyB)!.map((r) => Number(r.price)));
      if (minA !== minB) return minA - minB;
      return keyA.localeCompare(keyB); // 最低价也相同时按分组 key 排，保证顺序稳定确定
    });
    return orderedKeys.flatMap((key) =>
      [...groups.get(key)!].sort((a, b) => Number(a.price) - Number(b.price))
    );
  }, [results, groupMode, isAdmin, carrierFilter, productFilter]);

  // 分组 key -> 背景色，只有管理员账号才有意义；颜色按 displayedResults 里各分组出现的
  // 先后顺序分配，跟分组展示顺序保持一致，不是随机或者按 key 字母序。
  const groupColorMap = useMemo(() => {
    const map = new Map<string, string>();
    if (!isAdmin) return map;
    let colorIndex = 0;
    displayedResults.forEach((r) => {
      const key = groupKeyOf(r);
      if (!map.has(key)) {
        map.set(key, GROUP_ROW_COLORS[colorIndex % GROUP_ROW_COLORS.length]);
        colorIndex += 1;
      }
    });
    return map;
  }, [displayedResults, isAdmin, groupMode]);

  const currentValues = form.getFieldsValue();
  const insuranceAmountValue = Form.useWatch('insurance_amount', form);
  const packagesWatch = Form.useWatch('packages', form) || [];
  const totalPackageCount = packagesWatch.reduce((sum: number, pkg: PackageFormValue) => sum + Math.max(1, Math.round(pkg?.quantity || 1)), 0) || 1;

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>快递包裹物流费用查询</h3>
        <Space align="center" style={{ marginLeft: 40 }}>
          <Text>快速询价</Text>
          <Switch checked={quickQuote} onChange={setQuickQuote} disabled={locked} />
        </Space>
        {quickQuote && (
          <Text type="secondary" style={{ marginLeft: 12 }}>当前处于快速询价状态，为获得更准确的运费预估，请关闭此开关并填入完整地址信息。</Text>
        )}
        {locked && (
          <Text type="warning" style={{ marginLeft: 12 }}>当前报价已锁定，如需修改信息请点击下方"修改询价信息"。</Text>
        )}
      </div>
      <div style={{ marginBottom: 16, marginTop: 4 }}>
        <Text type="secondary">
          若从本司仓库发货或发货至本司仓库，可从仓库下拉列表选择后快速填入地址；可从地址库导入地址信息；美国地址可使用地址解析功能快速填入信息。
        </Text>
      </div>

      <Form<QuoteFormValues>
        form={form}
        layout="vertical"
        disabled={locked}
        onFinish={handleSubmit}
        onFinishFailed={() => Modal.warning({
          title: '信息未填写完整',
          content: '还有信息未填写完整，请检查页面中标红的字段',
          centered: true,
        })}
        initialValues={{
          origin: { country: 'US' },
          destination: { country: 'US' },
          packages: [{ quantity: 1 }],
        }}
      >
        <Row gutter={16} align="stretch">
          <Col xs={24} lg={12}>
            <PartyInfoPanel
              title="发件人信息"
              colorScheme={SECTION_COLORS.sender}
              namePath={['origin']}
              isRecipient={false}
              addressType="sender"
              quickQuote={quickQuote}
              restrictCountriesTo={ALLOWED_COUNTRIES}
              warehouseOptions={warehouseOptions}
              warehousesLoading={warehousesLoading}
              warehousePlaceholder="从本司仓库发货（快捷填入）"
              onSelectWarehouse={(id) => fillFromWarehouse('origin', id)}
            />
          </Col>
          <Col xs={24} lg={12}>
            <PartyInfoPanel
              title="收件人信息"
              colorScheme={SECTION_COLORS.recipient}
              namePath={['destination']}
              isRecipient
              addressType="recipient"
              quickQuote={quickQuote}
              restrictCountriesTo={ALLOWED_COUNTRIES}
              warehouseOptions={warehouseOptions}
              warehousesLoading={warehousesLoading}
              warehousePlaceholder="发货至本司仓库（快捷填入）"
              onSelectWarehouse={(id) => fillFromWarehouse('destination', id)}
            />
          </Col>
        </Row>

        <Row gutter={16} align="stretch" style={{ marginTop: 16 }}>
          <Col xs={24} lg={12}>
            <SectionCard
              title="包裹信息"
              colorScheme={SECTION_COLORS.packages}
              extra={(
                <div style={{ marginLeft: 28 }}>
                  <UnitToggle value={unitSystem} onChange={handleUnitToggle} />
                </div>
              )}
            >
              <Form.List name="packages">
                {(fields, { add, remove }) => (
                  <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    {fields.map((field, index) => (
                      <div
                        key={field.key}
                        style={{ border: '1px dashed #ffd591', borderRadius: 6, padding: 12 }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <Text strong>包裹类型{index + 1}</Text>
                          {fields.length > 1 && (
                            <MinusCircleOutlined onClick={() => remove(field.name)} style={{ color: '#999' }} />
                          )}
                        </div>
                        <Row gutter={12}>
                          <Col xs={24} md={10}>
                            {unitSystem === 'metric' ? (
                              <Form.Item label="重量" style={{ marginBottom: 24 }}>
                                <Space size={4} align="start">
                                  <Form.Item
                                    name={[field.name, 'weight_kg']}
                                    style={{ marginBottom: 0, width: 120 }}
                                  >
                                    <InputNumber min={0} step={1} precision={0} style={{ width: '100%' }} addonAfter="公斤" />
                                  </Form.Item>
                                  <Form.Item name={[field.name, 'weight_g']} style={{ marginBottom: 0, width: 120 }}>
                                    <InputNumber min={0} max={999} step={1} precision={0} style={{ width: '100%' }} addonAfter="克" />
                                  </Form.Item>
                                </Space>
                              </Form.Item>
                            ) : (
                              <Form.Item label="重量" style={{ marginBottom: 24 }}>
                                <Space size={4} align="start">
                                  <Form.Item
                                    name={[field.name, 'weight_lb']}
                                    style={{ marginBottom: 0, width: 120 }}
                                  >
                                    <InputNumber min={0} step={1} precision={0} style={{ width: '100%' }} addonAfter="磅" />
                                  </Form.Item>
                                  <Form.Item name={[field.name, 'weight_oz']} style={{ marginBottom: 0, width: 120 }}>
                                    <InputNumber min={0} max={15} step={1} precision={0} style={{ width: '100%' }} addonAfter="盎司" />
                                  </Form.Item>
                                </Space>
                              </Form.Item>
                            )}
                          </Col>
                          <Col xs={24} md={11}>
                            <Form.Item label="尺寸" required style={{ marginBottom: 24 }}>
                              <Space size={4} align="start">
                                <Form.Item
                                  name={[field.name, unitSystem === 'metric' ? 'length_cm' : 'length_in']}
                                  rules={[{ required: true, message: '请输入长度' }]}
                                  style={{ marginBottom: 0, width: 70 }}
                                >
                                  <InputNumber min={1} step={1} precision={0} placeholder="长" style={{ width: '100%' }} />
                                </Form.Item>
                                <Text style={{ lineHeight: '32px' }}>×</Text>
                                <Form.Item
                                  name={[field.name, unitSystem === 'metric' ? 'width_cm' : 'width_in']}
                                  rules={[{ required: true, message: '请输入宽度' }]}
                                  style={{ marginBottom: 0, width: 70 }}
                                >
                                  <InputNumber min={1} step={1} precision={0} placeholder="宽" style={{ width: '100%' }} />
                                </Form.Item>
                                <Text style={{ lineHeight: '32px' }}>×</Text>
                                <Form.Item
                                  name={[field.name, unitSystem === 'metric' ? 'height_cm' : 'height_in']}
                                  rules={[{ required: true, message: '请输入高度' }]}
                                  style={{ marginBottom: 0, width: 100 }}
                                >
                                  <InputNumber min={1} step={1} precision={0} placeholder="高" style={{ width: '100%' }} addonAfter={unitSystem === 'metric' ? '厘米' : '英寸'} />
                                </Form.Item>
                              </Space>
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={3}>
                            <Form.Item
                              {...field}
                              label="数量"
                              name={[field.name, 'quantity']}
                              initialValue={1}
                              rules={[{ required: true, message: '请输入数量' }]}
                            >
                              <InputNumber min={1} max={999} precision={0} style={{ width: '100%' }} />
                            </Form.Item>
                          </Col>
                        </Row>
                      </div>
                    ))}
                    <Button type="dashed" onClick={() => add({ quantity: 1 })} icon={<PlusOutlined />} block>
                      添加包裹类型
                    </Button>
                  </Space>
                )}
              </Form.List>
            </SectionCard>
          </Col>

          <Col xs={24} lg={12}>
            <SectionCard title="其他信息" colorScheme={SECTION_COLORS.other}>
              <Space size={6} align="center">
                <Form.Item name="signature_required" valuePropName="checked" style={{ marginBottom: 0 }}>
                  <Checkbox disabled={locked || signatureDisabledForNonUS}>需要签名服务</Checkbox>
                </Form.Item>
                {/* Tooltip 单独放在 Checkbox 外面，不受禁用状态影响，鼠标 hover 始终可以查看说明 */}
                <Tooltip title="仅美国境内运输支持直接选择签名服务。跨境运输及其他国家境内运输的签名服务是否可用，以承运商、服务类型及目的地国家/地区的实际规定为准">
                  <QuestionCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
                </Tooltip>
              </Space>
              <div style={{ marginBottom: 24 }} />

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: insuranceRequired ? 8 : 24 }}>
                <Form.Item name="insurance_required" valuePropName="checked" style={{ marginBottom: 0 }}>
                  <Checkbox>需要保险</Checkbox>
                </Form.Item>
                {insuranceRequired && (
                  <>
                    <Text>保险金额</Text>
                    <Form.Item
                      name="insurance_amount"
                      style={{ marginBottom: 0 }}
                      rules={[
                        { required: true, message: '请输入保险金额' },
                        {
                          validator: (_, value) => {
                            if (value == null) return Promise.resolve();
                            if (!Number.isInteger(value)) return Promise.reject(new Error('保险金额必须是正整数'));
                            if (value < 100 || value > 2000) return Promise.reject(new Error('保险金额范围为 100-2000 美金'));
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <InputNumber min={100} max={2000} step={1} precision={0} style={{ width: 160 }} addonAfter="美金" />
                    </Form.Item>
                  </>
                )}
              </div>
              {insuranceRequired && (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 24 }}
                  message="保险金额限最低 100，最高 2000 美金"
                  description={(
                    <>
                      <div>当存在多个包裹时，将平均分配保险金额至每个包裹（除不尽的余数计入第 1 个包裹）。</div>
                      {insuranceAmountValue ? <div>{describeInsuranceSplit(insuranceAmountValue, totalPackageCount)}</div> : null}
                    </>
                  )}
                />
              )}

              <Space size={6} align="center" style={{ marginBottom: 24 }}>
                <Form.Item name="contains_other_dangerous_goods" valuePropName="checked" style={{ marginBottom: 0 }}>
                  <Checkbox
                    onChange={(e) => {
                      if (e.target.checked) {
                        Modal.warning({
                          title: '暂不支持',
                          content: '暂不支持除电池以外的危险品运输，无法查询报价，请联系本司客服',
                          centered: true,
                        });
                      }
                    }}
                  >
                    包裹含危险品
                  </Checkbox>
                </Form.Item>
                <Popover
                  trigger="click"
                  title="常见危险品分类及举例"
                  content={(
                    <div style={{ maxWidth: 320 }}>
                      <div>易燃液体：香水、指甲油、酒精、打火机油</div>
                      <div>压缩气体：喷雾罐、打火机、氧气瓶</div>
                      <div>腐蚀性化学品：漂白剂、强酸强碱清洁剂</div>
                      <div>氧化剂：双氧水、漂白粉</div>
                      <div>毒性/感染性物质：农药、部分化学试剂、生物样本</div>
                      <div style={{ marginTop: 8, color: 'rgba(0, 0, 0, 0.45)' }}>电池不属于此类，请使用下方"包裹含电池"单独申报</div>
                    </div>
                  )}
                >
                  <QuestionCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)', cursor: 'pointer' }} />
                </Popover>
              </Space>

              <Form.Item
                name="battery_declared"
                valuePropName="checked"
                style={{ marginBottom: batteryDeclared ? 8 : 24 }}
              >
                <Checkbox>包裹含电池</Checkbox>
              </Form.Item>
              {batteryDeclared && (
                <div style={{ marginBottom: 24 }}>
                  <Form.Item
                    label={(
                      <Space size={4}>
                        电池种类
                        <Tooltip title="锂离子电池：可充电电池，如手机、笔记本电脑、蓝牙耳机、充电宝等使用的电池。锂金属电池：不可充电的一次性电池，如纽扣电池、部分相机电池等。">
                          <QuestionCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
                        </Tooltip>
                      </Space>
                    )}
                    name="battery_chemistry"
                    rules={[{ required: true, message: '请选择电池种类' }]}
                    style={{ marginBottom: 12 }}
                  >
                    <Radio.Group>
                      <Radio value="lithium_ion">锂离子电池</Radio>
                      <Radio value="lithium_metal">锂金属电池</Radio>
                    </Radio.Group>
                  </Form.Item>
                  <Form.Item
                    label="安装方式"
                    name="battery_installation"
                    rules={[{ required: true, message: '请选择电池安装方式' }]}
                    style={{ marginBottom: 12 }}
                  >
                    <Radio.Group>
                      <Radio value="installed">安装在设备内</Radio>
                      <Radio value="packed">随设备运输但未安装</Radio>
                      <Radio value="standalone">单独电池</Radio>
                    </Radio.Group>
                  </Form.Item>
                  <Form.Item
                    name="battery_condition_confirmed"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                    rules={[{
                      validator: (_, value) => (value
                        ? Promise.resolve()
                        : Promise.reject(new Error('请确认电池状态'))),
                    }]}
                  >
                    <Checkbox>电池完好无损，容量在消费电子产品常规豁免范围内</Checkbox>
                  </Form.Item>
                </div>
              )}

              <Form.Item label="参考号" name="reference_number">
                <Input />
              </Form.Item>
              <Form.Item label="备注信息" name="remarks">
                <Input.TextArea rows={3} />
              </Form.Item>
            </SectionCard>
          </Col>
        </Row>

      </Form>

      {/* 查询/重新询价按钮和下面这些筛选、分组、排查用的开关，特意放在 <Form> 外面：
          它们要么是"结果怎么展示"的纯前端筛选，要么(showAllGateways)虽然会触发重新查询，
          但改变的是查询参数本身而不是报价内容——都不属于"会让当前报价和页面显示的信息
          产生偏差"的字段，报价锁定时不应该被一起禁用，所以不能放在会被 disabled={locked}
          级联禁用的 <Form> 内部。 */}
      <div style={{ marginTop: 16, marginBottom: 24 }}>
        <Space align="center" wrap>
          {!isCustomer && (
            <Space align="center">
              <Select
                showSearch
                allowClear
                disabled={locked}
                placeholder="所属客户（可选，不指定则给通用预估）"
                style={{ width: 320 }}
                value={selectedQuoteCustomer?.id}
                onChange={(value) => setSelectedQuoteCustomer(quoteCustomerOptions.find((c) => c.id === value))}
                onSearch={handleQuoteCustomerSearch}
                onDropdownVisibleChange={(open) => { if (open) handleQuoteCustomerSearch(''); }}
                filterOption={false}
                loading={quoteCustomerSearchLoading}
                notFoundContent={quoteCustomerSearchLoading ? '搜索中...' : '未找到匹配的客户'}
                options={quoteCustomerOptions.map((c) => ({
                  value: c.id,
                  label: `${c.customer_code || '-'} ${c.company_name || c.username}`,
                }))}
              />
              <Tooltip title="不指定客户时，保证金金额按系统默认值给一个通用预估，看到的国际运单也不代表最终一定能买——实际以购买时选定的客户为准。指定客户后，这里的预估会按这个客户真实的风险设置计算。">
                <QuestionCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
              </Tooltip>
            </Space>
          )}
          <Tooltip title={!locked && containsOtherDangerousGoods ? '暂不支持除电池以外的危险品运输，请取消勾选"包裹含危险品"后再查询报价' : ''}>
            <Button
              type="primary"
              htmlType="button"
              loading={quoting}
              disabled={!locked && containsOtherDangerousGoods}
              onClick={() => (locked ? handleRequote() : form.submit())}
              style={locked ? { backgroundColor: '#fa541c', borderColor: '#fa541c' } : undefined}
            >
              {locked ? '修改询价信息' : '查询报价'}
            </Button>
          </Tooltip>
          <Select
            mode="multiple"
            allowClear
            placeholder="全部承运商"
            style={{ minWidth: 220 }}
            value={carrierFilter}
            onChange={setCarrierFilter}
            options={carrierOptions}
            maxTagCount="responsive"
          />
          <Select
            mode="multiple"
            allowClear
            placeholder="全部物流产品"
            style={{ minWidth: 220 }}
            value={productFilter}
            onChange={setProductFilter}
            options={productOptions}
            maxTagCount="responsive"
          />
          {isAdmin && (
            <Select
              value={groupMode}
              onChange={setGroupMode}
              style={{ width: 160 }}
              options={[
                { value: 'gateway', label: '按网关分组' },
                { value: 'product', label: '按物流产品分组' },
              ]}
            />
          )}
          {isAdmin && (
            <Space align="center">
              <Switch checked={showAllGateways} onChange={handleToggleShowAllGateways} />
              <Text>
                显示网关所有报价
                <Tooltip title="打开后不再按承运商/物流产品去重，把每个网关报出来的所有价格都显示出来（包括被更低价挤掉的），用于排查某个网关是否正常报价。切换后如果已经查询过，会自动用当前条件重新查一次。仅管理员可见，客户端不受影响。关闭时则仅显示每种物流产品的最低报价，相同最低报价以网关名称的字母顺序排序后仅显示第一位。">
                  <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                </Tooltip>
              </Text>
            </Space>
          )}
        </Space>
      </div>

      {quoteOverview && (
        <div style={{ marginBottom: 16 }}>
          <SectionCard
            title="询价概览"
            colorScheme={SECTION_COLORS.overview}
            extra={(
              <Tooltip title="费用说明：当前显示及支付的运费为预估费用，最终费用以承运商实际账单为准。承运商可能根据实际计费重量、体积、地址类型、配送区域等进行费用调整，包括但不限于重量差异、体积差异、偏远地区附加费、住宅地址附加费及其他相关费用。如包裹含电池，还可能产生危险品特殊处理费，或因电池种类/包装方式申报与实际不符等标识错误产生的额外附加费。">
                <Tag color="warning" style={{ cursor: 'default' }}>重要说明</Tag>
              </Tooltip>
            )}
          >
            <Descriptions bordered size="small" column={3}>
              <Descriptions.Item label="发货国家/邮编">
                {quoteOverview.originCountry} / {quoteOverview.originPostalCode || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="收货国家/邮编">
                {quoteOverview.destinationCountry} / {quoteOverview.destinationPostalCode || '-'}
              </Descriptions.Item>
              {quoteOverview.destinationCountry === 'US' && (
                <Descriptions.Item
                  label={(
                    <Space size={4}>
                      收件地址类型
                      <Tooltip title="仅美国收件地址需判断地址类型（商用或家庭地址），会影响物流费用；地址类型由本系统自动判断并仅用于运费预估，承运商最终账单可能会将地址判定为不同类型，并产生账单调整">
                        <QuestionCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
                      </Tooltip>
                    </Space>
                  )}
                >
                  {quoteOverview.isResidential == null ? '未知' : (quoteOverview.isResidential ? '住宅地址（仅供参考）' : '商业地址（仅供参考）')}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="包裹数量">{quoteOverview.packageCount} 件</Descriptions.Item>
              <Descriptions.Item label="总体积">
                {quoteOverview.totalVolumeDisplay} {quoteOverview.overviewUnitSystem === 'metric' ? '立方厘米' : '立方英寸'}
              </Descriptions.Item>
              <Descriptions.Item label="总重量">
                {formatWeightBreakdown(quoteOverview.totalWeightDisplay, quoteOverview.overviewUnitSystem)}
              </Descriptions.Item>
              <Descriptions.Item label="签名服务">{quoteOverview.signatureRequired ? '需要' : '不需要'}</Descriptions.Item>
              <Descriptions.Item label="保险" span={2}>
                {quoteOverview.insuranceRequired ? `需要（保额 ${quoteOverview.insuranceAmount} 美金）` : '不需要'}
              </Descriptions.Item>
              {quoteOverview.batteryDeclared && (
                <Descriptions.Item label="电池" span={3}>
                  {`含电池（${BATTERY_INSTALLATION_LABELS[quoteOverview.batteryInstallation!] || ''}）`}
                </Descriptions.Item>
              )}
            </Descriptions>
          </SectionCard>
        </div>
      )}

      <Table<ShippingRateQuoteResult>
        columns={columns}
        dataSource={displayedResults}
        rowKey={(record) => record.rate_quote_ids.join('-')}
        onRow={(record) => {
          const color = groupColorMap.get(groupKeyOf(record));
          return color ? { style: { backgroundColor: color } } : {};
        }}
        loading={quoting}
        pagination={{
          defaultPageSize: 50,
          pageSizeOptions: [10, 20, 50, 100],
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `共 ${total} 条报价 第${range[0]}-${range[1]}条`,
        }}
        locale={{ emptyText: hasQueried ? '没有查询到可用的物流报价' : '请填写上方信息后查询报价' }}
      />

      <PurchaseModal
        open={!!purchaseTarget}
        quote={purchaseTarget?.quote ?? null}
        signatureRequired={purchaseTarget?.signature_required}
        insuranceAmount={purchaseTarget?.insurance_amount}
        batteryDeclared={purchaseTarget?.battery_declared}
        batteryChemistry={purchaseTarget?.battery_chemistry}
        batteryInstallation={purchaseTarget?.battery_installation}
        origin={currentValues.origin}
        destination={currentValues.destination}
        overview={quoteOverview}
        packageOrigins={packageOrigins}
        remarks={currentValues.remarks}
        initialCustomer={selectedQuoteCustomer}
        onClose={() => setPurchaseTarget(null)}
        onSuccess={() => setPurchaseTarget(null)}
        onCreateNew={() => { setPurchaseTarget(null); handleCreateNewQuote(); }}
      />
    </Card>
  );
};

export default ParcelRateQuote;
